import { z } from 'zod';
import { RowVersionSchema } from '../common/row-version.js';
import { LanguageSchema } from '../common/language.js';
import { ProjectTierSchema } from './platform-responses.js';
import { WeekStartSchema } from '../common/period.js';
// Response schemas for /api/projects/:projectId/* — TWO audiences, two shapes:
//   ProjectSchema         → GET /api/projects/:projectId          (any member)
//   ProjectSettingsSchema → GET /api/projects/:projectId/settings (project_admin)
//
// Both follow ANATOMY.md §Doctrine: the field-exposure rule — minimum data the
// caller needs for its current job. ProjectSchema exposes the brand/identity
// fields, `type`, the two trial columns, the currency, the zone, the week start
// and the default language — each with a named consumer; ProjectSettings exposes the admin slice
// on top.
//
// Columns still exposed NOWHERE, and where they will land:
//   logo_web                                              → first branded-platform customer ask
//   mother_company / mother_company_address               → GET /api/projects/:projectId/billing-info (invoicing surface; sensitive — never on member-read)
//   invoicing_cycle_duration /
//     invoicing_first_cycle_start_on                      → the invoicing slice's own settings surface
//   suspended_at / terminated_at / created_by / created_at → /api/platform/projects (sysadmin)
//
// `type` is exposed on the member read because the ProjectBanner shows a "Demo"
// indicator for `type === 'demo'` — a real member consumer. The enum itself is
// non-sensitive project metadata. The `has_*` entitlement flags reach a member's
// UI through `/me` (ADR-044), never through this file.
const HEX_COLOR = /^#[0-9a-fA-F]{6}$/;
// GET /api/projects/:projectId — member-read project metadata.
// Caller: useProject(projectId) hook consumed by ThemeProvider + any component
// displaying the project's name.
export const ProjectSchema = z
    .object({
    id: z.string().max(16),
    name: z.string().max(32),
    display_name: z.string().max(64).nullable(),
    type: z.enum(['esco', 'embedded', 'demo']),
    color_primary: z.string().regex(HEX_COLOR),
    color_on_primary: z.string().regex(HEX_COLOR),
    status: z.enum(['active', 'suspended', 'terminated']),
    // Commercial trial (orthogonal to `type`). Consumer: the project banner —
    // `is_trial` + `trial_expires_at` drive the "X days left · Contact us"
    // countdown; the TRIAL_EXPIRED access gate is keyed on the same columns
    // server-side. `type === 'demo'` is the separate sample-data signal.
    is_trial: z.boolean(),
    trial_expires_at: z.string().datetime().nullable(),
    // The base currency — the DEFAULT of every money field a member enters (the asset books), never a rule: an amount
    // keeps the currency it was entered in (common/currency).
    default_currency: z.string().length(3),
    // The project's zone (ADR-082 §2 · §3). Consumer: `useDisplayZone` — every project screen (the hub, the
    // registers, the lists) shows its times in it and names it ("project time") to a viewer in another zone.
    timezone_iana: z.string().max(64),
    // The first day of the week (ISO 1–7 — ADR-082 §4). Consumer: the period picker ("This week" starts on it) and
    // every calendar grid (the DatePicker's first column) — never the language's.
    week_start: WeekStartSchema,
    // The project's default language. Consumer: the user wizard pre-fills a new account's language from it.
    default_language: LanguageSchema,
})
    .strict();
// GET /api/projects/:projectId/settings — the project-admin settings surface
// (gate: sysadmin OR project_admin). Caller: pages/admin/project-settings.
//
// ONE payload carrying BOTH halves of the page, because the read-only half is
// what makes the editable half legible — "default country" can't be judged
// without the country whitelist it must sit inside, and the plan explains why
// the licensing section has no controls. Both halves share one gate, so this is
// one audience, not two (ANATOMY §field-exposure: different audience = different
// endpoint; SAME audience = one endpoint).
//
// The editable subset is UpdateProjectSettingsRequestSchema in requests.ts; every
// other field here is vendor-owned and read-only on this surface.
export const ProjectSettingsSchema = z
    .object({
    row_version: RowVersionSchema,
    // — identity —
    id: z.string().max(16),
    name: z.string().max(32), // the code — immutable; a machine reference, not shown
    display_name: z.string().max(64).nullable(),
    description: z.string().nullable(),
    status: z.enum(['active', 'suspended', 'terminated']),
    // — brand (BrandFields; shared verbatim with the platform brand drawer) —
    color_primary: z.string().regex(HEX_COLOR),
    color_on_primary: z.string().regex(HEX_COLOR),
    // — regional defaults —
    default_country_iso_code: z.string().length(2).nullable(),
    timezone_iana: z.string().max(64),
    week_start: WeekStartSchema,
    default_currency: z.string().length(3),
    default_language: LanguageSchema,
    // — site defaults (what a new site inherits; each site overrides) —
    use_gateway_by_default: z.boolean(),
    default_polling_rate_s: z.number().int().nullable(),
    // — the Files policy (ADR-068 §2) + numbers —
    files_review_undescribed: z.boolean(),
    files_review_off_site: z.boolean(),
    files_review_no_position: z.boolean(),
    files_trust_technicians: z.boolean(),
    visit_gps_tolerance_m: z.number().int(),
    visit_nearby_radius_m: z.number().int(),
    visit_expiry_hours: z.number().int(),
    files_bin_grace_days: z.number().int(),
    // — the ticket settings (ADR-073) —
    ticket_capture_grace_days: z.number().int().nullable(),
    ticket_dormant_hours: z.number().int(),
    // — read-only: the plan (sysadmin-owned; ADR-055) —
    type: z.enum(['esco', 'embedded', 'demo']),
    is_trial: z.boolean(),
    trial_expires_at: z.string().datetime().nullable(),
    tiers: z.array(ProjectTierSchema),
    // Each whitelisted country carries its seeded default zone, week start and
    // currency (`countries.default_timezone_iana` · `default_week_start` ·
    // `default_currency_code`) — the regional drawer's country→zone and
    // country→week-start pre-fills and the currency field's start list read
    // them, so the hints come from the reference table rather than a client-side
    // map. One zone per country is a HINT, never a truth: a multi-zone country is
    // corrected here and authoritatively per site (ADR-030).
    countries: z.array(z.object({
        iso_code: z.string().length(2),
        default_timezone_iana: z.string().max(64).nullable(),
        default_week_start: WeekStartSchema,
        default_currency_code: z.string().length(3).nullable(),
    }).strict()),
    has_invoicing: z.boolean(),
    has_field_force: z.boolean(),
    has_vehicles: z.boolean(),
})
    .strict();
